import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RENDER_BUSY_MINUTES, VIDEO_STYLES, canAfford, monthAgainstCap, planFor, reelPhotos, reelPhotosSaid, showreelVideo, voiceName, type ProjectView, type VideoStyle } from "@joogoos/contracts";
import { api } from "../../api.js";
import { AtYourLimit } from "../../ui/money.js";
import { GenerateButton } from "../Chat.js";

const FORMAT_LABEL: Record<string, string> = {
  square: "square, for the feed", portrait: "portrait 4:5", story: "story 9:16", landscape: "landscape 16:9",
};

/**
 * Zone 3 once every question is answered: the pipeline, written out (03).
 *
 * Not the recap card it replaced. The question at this moment is not "what did I say" — the chat above is the
 * record of that — it is "what is about to happen and what will it cost", and that is a list of steps with prices
 * on them. The graph is drawn the moment it starts running; before it runs, it reads better than it draws.
 *
 * It is also where the money is checked **before** anything runs
 * ([ADR 0012](../../../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md) point 2): the total against the
 * balance, this month against the month's ceiling, and today against the day's. `runCall` checks all three again as
 * its backstop, and has to — but a half-run pipeline is a poor way to learn about a ceiling, and the alternative is
 * arithmetic somebody can read next to the button.
 */
export function Plan({ project }: { project: ProjectView }) {
  const qc = useQueryClient();
  // The owner's photos, for a video of pieces (Showreel, Editorial, Poster, Handmade) with some chosen: it shows only
  // those a photo piece may, and the plan counts only those (review, 2026-09-29). The picker's own list, so it is
  // usually already here.
  const reelWithPhotos = showreelVideo(project.draft) && !!project.draft.sourceMediaIds?.length;
  const library = useQuery({ queryKey: ["media", "image"], queryFn: () => api.media("image"), enabled: reelWithPhotos });
  const plan = planFor(project.draft, library.data);
  const ws = useQuery({ queryKey: ["workspace"], queryFn: api.workspace });
  // How long the render queue is, said before Generate rather than discovered after (docs/10 § Queues).
  const wait = useQuery({ queryKey: ["render-wait"], queryFn: api.renderWait, staleTime: 30_000 });
  const busy = wait.data && wait.data.minutes >= RENDER_BUSY_MINUTES ? wait.data.minutes : null;
  const generate = useMutation({
    mutationFn: () => api.generate(project.id),
    onSuccess: (p) => { qc.setQueryData(["project", p.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
  const d = project.draft;
  const photos = d.sourceMediaIds?.length ?? 0;
  const drawn = d.kind === "video" && d.look === "drawn";
  // The type of a video of pieces is the caption, so its voice says the words on screen; and a video with no voice has
  // no captions in any style, which this sentence promised until 2026-09-28.
  const narration = d.voice?.enabled ? `, narrated by ${voiceName(d.voice.voiceId)}${showreelVideo(d) ? ", who says the words on screen" : ""}` : ", with no narration";
  // A video of pieces shows the photos chosen inside its photo pieces, and is still drawn (2026-09-29); no other drawn
  // style shows one. Said as the plan's step says it, never more than it has pieces for (review, 2026-09-29).
  const inReel = reelPhotosSaid(reelPhotos(d, library.data));
  const pictures = drawn
    ? inReel ? `drawn, with ${inReel}` : "drawn, with no pictures to make"
    : photos ? `using ${photos} of your photos` : "with pictures I generate";
  // Pictures or drawn (ADR 0050), answered like any other chip: free, and the plan below changes with it.
  const look = useMutation({
    mutationFn: (to: "pictures" | "drawn") => api.answer(project.id, to === "drawn" ? { look: "drawn" } : { look: "pictures" }, to === "drawn" ? "Draw it" : "Use pictures"),
    onSuccess: (p) => qc.setQueryData(["project", p.id], p),
  });
  // The style a drawn video is drawn in, the same way: free, and a chip in the chat says what was chosen.
  const style = useMutation({
    mutationFn: (to: VideoStyle) => api.answer(project.id, { style: to }, `Style: ${VIDEO_STYLES.find((s) => s.id === to)!.name}`),
    onSuccess: (p) => qc.setQueryData(["project", p.id], p),
  });
  const styleNow = d.style ?? "clean";
  // Music drawn by code (ADR 0054): on unless turned off, and free either way.
  const music = useMutation({
    mutationFn: (on: boolean) => api.answer(project.id, { music: on }, on ? "With music" : "No music"),
    onSuccess: (p) => qc.setQueryData(["project", p.id], p),
  });
  const musicOn = d.music !== false;

  /**
   * Whether this can be paid for. Unknown until the workspace has answered, and *pressing is allowed* until then:
   * a Generate button greyed out because a read is in flight is a bug that looks like a policy, and the guards
   * behind it refuse anything this would have caught.
   */
  const money = ws.data ? canAfford(plan.totalUsd, ws.data) : { ok: true as const };
  const month = monthAgainstCap(ws.data?.monthUsd, ws.data?.monthlyCapUsd);
  // Dismissed with "Carry on by hand": the ceiling is still there and the button still refuses, but the dialog has
  // been read and does not need to sit on the screen (ADR 0012 point 4).
  const [dismissed, setDismissed] = useState(false);

  return (
    <div className="p-4 md:p-6 flex flex-col gap-4 h-full min-h-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-lg font-semibold">Here is what I will do</h1>
        <span className="text-[13px] text-muted">nothing has been spent yet</span>
      </div>

      <p className="text-[14px] leading-relaxed max-w-[62ch]">
        {d.kind === "post" ? "A post" : "A short video"} about <b className="font-semibold">{d.idea}</b>,{" "}
        {FORMAT_LABEL[d.format ?? "story"]}, {pictures}
        {d.kind === "video" ? narration : ""}
        {d.onScreenText ? `, and “${d.onScreenText}” on screen` : ""}.
      </p>

      {d.kind === "video" ? (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="What the shots are made of">
          <span className="text-[12.5px] text-muted mr-1">Made of</span>
          {([["pictures", photos ? "Your photos" : "Pictures"], ["drawn", "Drawn scenes"]] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => look.mutate(k)}
              disabled={look.isPending}
              aria-pressed={(k === "drawn") === drawn}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${(k === "drawn") === drawn ? "bg-ink text-white" : "bg-page text-ink-2 hover:bg-line"}`}
            >
              {label}
            </button>
          ))}
          <span className="text-[12px] text-muted">{drawn ? "headlines, numbers, steps and painted pictures, in your colours" : "drawn scenes cost nothing to make"}</span>
        </div>
      ) : null}

      {drawn ? (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="The style it is drawn in">
          <span className="text-[12.5px] text-muted mr-1">Style</span>
          {VIDEO_STYLES.map((s) => (
            <button
              key={s.id}
              onClick={() => style.mutate(s.id)}
              disabled={style.isPending}
              aria-pressed={s.id === styleNow}
              title={`${s.look} — suits ${s.suits}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${s.id === styleNow ? "bg-ink text-white" : "bg-page text-ink-2 hover:bg-line"}`}
            >
              {s.name}
            </button>
          ))}
          <span className="text-[12px] text-muted">{VIDEO_STYLES.find((s) => s.id === styleNow)!.look}</span>
        </div>
      ) : null}

      {d.kind === "video" ? (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Music">
          <span className="text-[12.5px] text-muted mr-1">Music</span>
          {([[true, "On"], [false, "Off"]] as const).map(([on, label]) => (
            <button
              key={label}
              onClick={() => music.mutate(on)}
              disabled={music.isPending}
              aria-pressed={on === musicOn}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${on === musicOn ? "bg-ink text-white" : "bg-page text-ink-2 hover:bg-line"}`}
            >
              {label}
            </button>
          ))}
          <span className="text-[12px] text-muted">{musicOn ? "composed for this video, free" : "silent under the words"}</span>
        </div>
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto">
        <ol className="bg-panel border border-line rounded-[10px] divide-y divide-line">
          {plan.steps.map((s, i) => (
            <li key={s.key} className="flex items-baseline gap-3 px-4 py-2.5">
              <span className="w-5 shrink-0 text-[11px] tabular-nums text-faint">{i + 1}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-medium">{s.title}</span>
                <span className="block text-[12px] text-muted leading-snug">{s.detail}</span>
              </span>
              <span className="shrink-0 text-[12.5px] tabular-nums text-muted">{s.usd ? `$${s.usd.toFixed(2)}` : "free"}</span>
            </li>
          ))}
        </ol>
        <p className="text-[12px] text-muted leading-relaxed mt-3 max-w-[62ch]">
          Each step is charged for what it actually uses, which is why this says about. Once it is made, a shot
          can be run again on its own.
        </p>
      </div>

      {busy ? (
        <p role="status" className="text-[13px] bg-page border border-line rounded-[10px] px-4 py-2.5">
          <b className="font-semibold">Busy right now:</b> your video starts in about {busy} minutes. You can leave the page; it carries on.
        </p>
      ) : null}

      {/* The ceiling, before the button and not after half a pipeline (ADR 0012 point 3). */}
      {!money.ok && !dismissed ? <AtYourLimit why={money} onCarryOn={() => setDismissed(true)} /> : null}

      <div className="bg-panel border border-line rounded-[10px] px-4 py-3.5 flex items-center gap-4">
        <div>
          <div className="text-[15px] font-semibold tabular-nums">About ${plan.totalUsd}</div>
          <div className="text-[12px] text-muted">
            ${ws.data?.balanceUsd ?? "—"} of credit left
            {/* Said before the last affordable post rather than at it (ADR 0012 point 6). */}
            {month ? <> · {month}</> : null}
          </div>
        </div>
        <span className="ml-auto">
          <GenerateButton
            plan={plan.totalUsd}
            busy={generate.isPending}
            stopped={!money.ok}
            onClick={() => (money.ok ? generate.mutate() : setDismissed(false))}
          />
        </span>
      </div>
      {generate.error ? <p className="text-bad text-xs">{generate.error.message}</p> : null}
    </div>
  );
}
